import React, { useContext, useState } from 'react'
import { Box, Grid, Button, TextField, Select, MenuItem, FormControl, InputLabel, FormHelperText } from '@mui/material'
import { Form, Field } from 'react-final-form'
import { SettingsContext } from '../../../../../App'
import EmployeeTable from './EmployeeTable'
import { useEmployeeTable } from './useEmployeeTable'
import { useOnboardingContext } from '../../../context/OnboardingContext'
import { useBulkImportUsersMutation } from '../../../../../api/onboardingApiSlice'
import { useMutation } from '../../../../../hooks/useMutation'

const validate = values => {
  const errors = {}
  if (!values.firstName) {
    errors.firstName = 'First name is required'
  } else if (values.firstName.length > 40) {
    errors.firstName = 'Max 40 chars.'
  }
  if (!values.lastName) {
    errors.lastName = 'Last name is required'
  } else if (values.lastName.length > 40) {
    errors.lastName = 'Max 40 chars.'
  }
  if (!values.email) {
    errors.email = 'Email is required'
  } else if (!/\S+@\S+\.\S+/.test(values.email)) {
    errors.email = 'Invalid email format'
  } else if (values.email.length > 100) {
    errors.email = 'Max 100 chars.'
  }
  if (!values.systemRole) {
    errors.systemRole = 'Employee type is required'
  }
  return errors
}

export default function AddManuallyTab() {
  const [settings] = useContext(SettingsContext)
  const {
    employees,
    addOrEditEmployee,
    selectForEdit,
    deleteEmployee,
    editingIndex,
    initialValues,
    setEditingIndex,
    clearAllEmployees
  } = useEmployeeTable()

  const { handleCompleteStep, OnboardingStepType, allCards: allSteps } = useOnboardingContext()

  const [submissionStatus, setSubmissionStatus] = useState('idle')

  const { submit: bulkImportUsers, isLoading: isImportingUsers } = useMutation(useBulkImportUsersMutation, {
    successMessage: 'Employees added successfully!',
    onSuccess: () => {
      setSubmissionStatus('success')
      const importStep = allSteps?.find(step => step.apiType === OnboardingStepType.IMPORT_EMPLOYEES)

      if (importStep && importStep.status !== 'COMPLETED' && handleCompleteStep) {
        handleCompleteStep(OnboardingStepType.IMPORT_EMPLOYEES)
      }

      if (clearAllEmployees) {
        clearAllEmployees()
      }
    },
    onError: () => {
      setSubmissionStatus('error')
    }
  })

  const departmentOptions = settings?.departments || []
  const locationOptions = settings?.countries || []
  const languageOptions = settings?.languages || []

  const employeeTypeOptions = [
    { value: 'USER', label: 'Employee' },
    { value: 'COMPANY_ADMIN', label: 'Company Admin' }
  ]

  const handleSubmitEmployees = async () => {
    setSubmissionStatus('pending')
    try {
      await bulkImportUsers(employees)
    } catch (error) {
      if (submissionStatus !== 'error') {
        setSubmissionStatus('error')
      }
    }
  }

  return (
    <Box sx={{ p: 3, backgroundColor: '#F9FAFB', minHeight: '100%' }}>
      <Box sx={formWrapperSx}>
        <Form
          onSubmit={(values, form) => {
            addOrEditEmployee(values)

            form.restart()
          }}
          validate={validate}
          initialValues={initialValues}
          key={editingIndex}
          render={({ handleSubmit, submitting }) => (
            <form onSubmit={handleSubmit} autoComplete='off'>
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                  <Field name='firstName' component={TextFieldAdapter} label='First name' />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field name='lastName' component={TextFieldAdapter} label='Last name' />
                </Grid>
                <Grid item xs={12}>
                  <Field name='email' component={TextFieldAdapter} label='Email' type='email' />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field name='systemRole' component={SelectAdapter} label='Employee type'>
                    {employeeTypeOptions.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Field>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field name='department' component={SelectAdapter} label='Department (optional)'>
                    <MenuItem value=''>
                      <em>None</em>
                    </MenuItem>
                    {departmentOptions.map(dep => (
                      <MenuItem key={dep} value={dep}>
                        {dep}
                      </MenuItem>
                    ))}
                  </Field>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field name='country' component={SelectAdapter} label='Location (optional)'>
                    <MenuItem value=''>
                      <em>None</em>
                    </MenuItem>
                    {locationOptions.map(loc => (
                      <MenuItem key={loc} value={loc}>
                        {loc}
                      </MenuItem>
                    ))}
                  </Field>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field name='language' component={SelectAdapter} label='Language (optional)'>
                    <MenuItem value=''>
                      <em>None</em>
                    </MenuItem>
                    {languageOptions.map(lang => (
                      <MenuItem key={lang} value={lang}>
                        {lang}
                      </MenuItem>
                    ))}
                  </Field>
                </Grid>
                <Grid item xs={12}>
                  <Button type='submit' variant='outlined' fullWidth sx={buttonSx} disabled={submitting}>
                    {editingIndex !== null ? 'Save' : 'Add'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          )}
        />
      </Box>
      {employees.length > 0 && (
        <EmployeeTable
          employees={employees}
          employeeTypeOptions={employeeTypeOptions}
          onRowClick={selectForEdit}
          onDelete={deleteEmployee}
          editingIndex={editingIndex}
        />
      )}
      {employees.length > 0 && submissionStatus !== 'error' && (
        <Button
          variant='contained'
          color='primary'
          fullWidth
          sx={submitButtonSx}
          onClick={handleSubmitEmployees}
          disabled={isImportingUsers || submissionStatus === 'pending' || employees.length === 0}
        >
          {submissionStatus === 'pending'
            ? 'Adding...'
            : `Add ${employees.length} employee${employees.length > 1 ? 's' : ''}`}
        </Button>
      )}
      {submissionStatus === 'error' && (
        <Button
          variant='outlined'
          color='error'
          fullWidth
          sx={{ ...submitButtonSx, mt: 1, borderColor: 'error.main', color: 'error.main' }}
          onClick={() => setSubmissionStatus('idle')}
        >
          Try Again
        </Button>
      )}
    </Box>
  )
}

const TextFieldAdapter = ({ input, meta, ...rest }) => (
  <TextField
    {...input}
    {...rest}
    error={meta.touched && !!meta.error}
    helperText={meta.touched && meta.error ? meta.error : ''}
    variant='outlined'
    size='small'
    fullWidth
    sx={textFieldSx}
  />
)

const SelectAdapter = ({ input, meta, label, children, ...rest }) => (
  <FormControl fullWidth error={meta.touched && !!meta.error} size='small' sx={formControlSx}>
    <InputLabel>{label}</InputLabel>
    <Select
      {...input}
      {...rest}
      label={label}
      sx={textFieldSx}
      value={input.value || ''}
      onChange={e => input.onChange(e.target.value)}
    >
      {children}
    </Select>
    {meta.touched && meta.error && <FormHelperText>{meta.error}</FormHelperText>}
  </FormControl>
)

const textFieldSx = {
  fontFamily: 'Plus Jakarta Sans',
  '& .MuiOutlinedInput-root': {
    fontFamily: 'Plus Jakarta Sans',
    borderRadius: '14px',
    height: '60px',
    backgroundColor: '#fff'
  },
  '& .MuiOutlinedInput-notchedOutline': {
    fontFamily: 'Plus Jakarta Sans',
    border: '1px solid #E7E7EE'
  },
  '& .MuiInputBase-input': {
    fontFamily: 'Plus Jakarta Sans',
    fontWeight: 400,
    fontSize: '16px'
  }
}

const formWrapperSx = {
  width: '100%',
  padding: '16px',
  border: '1px solid #DBDBE6',
  borderRadius: '12px',
  backgroundColor: '#fff'
}
const submitButtonSx = {
  borderRadius: '16px',
  fontFamily: 'Plus Jakarta Sans',
  fontWeight: 400,
  fontSize: '16px',
  padding: '8px 8px',
  backgroundColor: '#0246F8',
  boxShadow: 'none',
  color: '#fff',
  textTransform: 'none',
  mt: 2
}

const buttonSx = {
  borderRadius: '16px',
  fontFamily: 'Plus Jakarta Sans',
  fontWeight: 400,
  fontSize: '16px',
  padding: '8px 8px',
  border: '1px solid #0246F8',
  color: '#0246F8',
  boxShadow: 'none',
  textTransform: 'none'
}
const formControlSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '14px',
    height: '60px',
    backgroundColor: '#fff'
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: '1px solid #E7E7EE'
  },
  '& .MuiInputBase-input': {
    fontFamily: 'Plus Jakarta Sans',
    fontWeight: 400,
    fontSize: '16px'
  }
}
